import { createContext, useContext } from 'react'

/**
 * Zeitleiste der Office-Version. Jede Station hat einen Bereich auf --l
 * (0..1 über die ganze Section). Animationen innerhalb einer Station werden
 * mit `inB` relativ angegeben, so lassen sich Stationen verschieben, ohne
 * jede Zahl anzufassen.
 */

export const BEATS = [
  { id: 'intro', label: 'Das Büro', at: [0, 0.03] },
  {
    id: 'vorstory',
    label: 'Die offene Stelle',
    at: [0.03, 0.085],
    text: 'Dein Unternehmen sucht Verstärkung im Customer Success. Im Team ist ein Platz frei, und der soll richtig gut besetzt werden.'
  },
  {
    id: 'hr',
    label: 'HR startet mit Aivy',
    at: [0.085, 0.15],
    text: 'Mara aus dem HR nutzt Aivy für die Vorauswahl. Sie startet mit einer Aivy-Vorlage für die Stelle, bindet die Fachbereiche ein oder kombiniert beides.'
  },
  {
    id: 'analyse',
    label: 'Die Anforderungsanalyse',
    at: [0.15, 0.235],
    text: 'Die Fachbereiche definieren die Anforderungen selbst, jede Person für sich in der Aivy Anforderungsanalyse. HR hat keinen Aufwand, und am Ende sprechen alle dieselbe Sprache.'
  },
  {
    id: 'profil',
    label: 'Das Anforderungsprofil',
    at: [0.235, 0.29],
    text: 'Aivy berechnet aus den Analysen das Anforderungsprofil. Für jede Anforderung gibt es einen Ideal- und einen Übergangsbereich. Das ist die Grundlage fürs Matching.'
  },
  {
    id: 'bewerbungen',
    label: 'Die Bewerbungen',
    at: [0.29, 0.36],
    text: 'Dann kommen die Bewerbungen. Viele Bewerbungen. Lebensläufe allein verraten aber wenig darüber, wer wirklich passt.'
  },
  {
    id: 'games',
    label: 'Game-based Assessment',
    at: [0.36, 0.44],
    text: 'Aivy schickt allen Bewerbenden das Game-based Assessment. Kurze Spiele am Smartphone oder im Web, die Ergebnisse kommen direkt zurück.'
  },
  {
    id: 'magic',
    label: 'Ein Blick in Aivy',
    at: [0.44, 0.51],
    text: 'Was passiert in Aivy? Aus dem Spielverhalten werden validierte Dimensionen. Die gleicht Aivy mit dem Anforderungsprofil ab und berechnet daraus die Passung.'
  },
  {
    id: 'regeln',
    label: 'Regelbasiert',
    at: [0.51, 0.565],
    text: 'Die Passung ist regelbasiert. Kein Modell, das Menschen nach unklaren Mustern bewertet, sondern feste, transparente Regeln.'
  },
  {
    id: 'talente',
    label: 'Bewerbende lieben es',
    at: [0.565, 0.62],
    text: 'Und die Bewerbenden? Bekommen am Ende ihr persönliches Stärkenprofil, komplett kostenlos. Das schafft Akzeptanz, denn spielen macht einfach mehr Spaß als Fragebögen.'
  },
  {
    id: 'report',
    label: 'Shortlist und Report',
    at: [0.62, 0.7],
    text: 'Mara bekommt eine Shortlist. Das Kernstück ist das ausführliche Passungsprofil als PDF. Der Aivy Assistent hilft zusätzlich, die Daten schnell und einfach abzufragen.'
  },
  {
    id: 'gespraech',
    label: 'Das Gespräch',
    at: [0.7, 0.78],
    text: 'Lena kommt zum Gespräch. Die Interviewfragen schlägt Aivy direkt auf Basis ihres Passungsprofils vor, genau zu den Punkten, die sich zu vertiefen lohnen.'
  },
  {
    id: 'team',
    label: 'Das neue Team',
    at: [0.78, 0.835],
    text: 'Lena setzt sich zu ihrem neuen Team. Sie passt zur Aufgabe und zur Kultur im Team, alle freuen sich.'
  },
  {
    id: 'absagen',
    label: 'Faire Absagen',
    at: [0.835, 0.89],
    text: 'Auch die Absagen fallen leichter. Alle wurden nach denselben Kriterien bewertet, datenbasiert und fair. Das gibt HR Sicherheit, auch rechtlich.'
  },
  {
    id: 'ats',
    label: 'Im eigenen ATS',
    at: [0.89, 0.93],
    text: 'Aivy läuft auch in deinem ATS. Mit dem Sidebar-Plugin hast du den Assistenten und die volle Auswertung direkt im System, in dem du ohnehin arbeitest.'
  },
  {
    id: 'support',
    label: 'Support',
    at: [0.93, 0.958],
    text: 'Und wenn mal eine Frage offen ist? Unser Support hilft dir gern, schnell und persönlich.'
  },
  { id: 'finale', label: 'Alle happy', at: [0.958, 1] }
]

export const B = Object.fromEntries(BEATS.map((b) => [b.id, b.at]))

/** Bereich [a, b] relativ zur Station `beat` (0 = Anfang, 1 = Ende). */
export const inB = (beat, a, b) => {
  const [s, e] = B[beat]
  const len = e - s
  return [s + a * len, s + b * len]
}

/** Einzelner Zeitpunkt relativ zur Station. */
export const atB = (beat, a) => inB(beat, a, a)[0]

/**
 * Panels holen sich die Zeitleiste über diesen Context. Ohne Provider gilt
 * die Office-Version, andere Versionen reichen ihre eigenen Stationen mit
 * denselben ids hinein.
 */
const BeatsContext = createContext({ inB, atB })
export const BeatsProvider = BeatsContext.Provider
export const useBeats = () => useContext(BeatsContext)
